import * as React from 'react';
import * as echarts from 'echarts/core';
import { PieChart } from 'echarts/charts';
import { TooltipComponent, LegendComponent } from 'echarts/components';
import { SVGRenderer } from 'echarts/renderers';
import { Box, Typography } from '@mui/material';
import type {
  LegendComponentOption,
  PieSeriesOption,
  TooltipComponentOption,
} from 'echarts';

type ECOption = echarts.ComposeOption<
  PieSeriesOption | TooltipComponentOption | LegendComponentOption
>;

echarts.use([PieChart, TooltipComponent, LegendComponent, SVGRenderer]);

const AppDownloadPieChart: React.FC<{
  android: number;
  ios: number;
}> = ({ android, ios }) => {
  const chartRef = React.useRef<HTMLDivElement>(null);
  const chartInstance = React.useRef<echarts.ECharts | null>(null);
  const total = android + ios;

  React.useEffect(() => {
    if (!chartRef.current) return;
    if (!chartInstance.current) {
      chartInstance.current = echarts.init(chartRef.current);
    }
    const option: ECOption = {
      tooltip: {
        trigger: 'item',
        formatter: (params: any) =>
          `${params.name}: ${Number(params.value).toLocaleString()} (${params.percent}%)`,
      },
      legend: { orient: 'vertical', right: 0, top: 'middle' },
      series: [
        {
          name: 'Downloads',
          type: 'pie',
          radius: ['42%', '72%'],
          center: ['40%', '50%'],
          data: [
            { name: 'Android', value: android, itemStyle: { color: '#3DDC84' } },
            { name: 'iOS', value: ios, itemStyle: { color: '#555555' } },
          ],
        },
      ],
    };
    chartInstance.current.setOption(option, true);
    const resizeHandler = () => chartInstance.current?.resize();
    window.addEventListener('resize', resizeHandler);
    return () => {
      window.removeEventListener('resize', resizeHandler);
      chartInstance.current?.dispose();
      chartInstance.current = null;
    };
  }, [android, ios]);

  if (!total) {
    return (
      <Typography variant="body2" color="text.secondary">
        No downloads in this date range yet.
      </Typography>
    );
  }

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1, textAlign: 'center' }}>
        {total.toLocaleString()} downloads in the selected range
      </Typography>
      <div ref={chartRef} style={{ width: '100%', height: 320 }} />
    </Box>
  );
};

export default AppDownloadPieChart;
